import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { UploadCloud, Images, Layers, Check, CornerUpLeft, Loader2, ArrowLeft, ArrowRight, RotateCcw, ZoomIn, Trash2 } from 'lucide-react';
import { supabase } from '../../supabaseClient';
import { mediaUrl, IMG } from '../../lib/mediaUrl';
import { shrinkImage } from '../../lib/shrinkImage';
import Lightbox from '../../components/Lightbox';
import ConfirmDialog from '../../components/ConfirmDialog';
import { deleteProduct } from '../../lib/deleteProduct';
import { shippingDefaultsFor } from '../../lib/shippingDefaults';

const MEDIA_BUCKET = 'shinyvault-media';

// Where loose bulk photos live before they become real products. Matched by
// AdminProductEditPage's per-product media paths, but kept in their own prefix
// so a staging photo is never confused with an attached product photo.
const STAGING_PREFIX = 'bulk-staging';

// localStorage key for the lightweight wizard draft. The photos already live in
// the storage bucket, so persisting { photos, items, step } is enough to survive
// a refresh or accidental close without re-uploading anything.
const DRAFT_KEY = 'shinyvault-bulk-draft';

// localStorage key for the Step 4 review batch — a JSON array of the created
// product id strings. Kept separate from DRAFT_KEY (photos/items) so that
// leaving Step 4 to edit an item in the standalone editor and coming back can
// re-hydrate the exact same batch with each item's latest saved name/price.
const REVIEW_KEY = 'shinyvault-bulk-review';

// Short, collision-resistant token for draft slugs (products.slug is unique).
function shortId() {
  return Math.random().toString(36).slice(2, 8) + Date.now().toString(36).slice(-4);
}

// Read the persisted wizard draft once, defensively (bad JSON -> empty draft).
function loadDraft() {
  try {
    const raw = localStorage.getItem(DRAFT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    return {
      photos: Array.isArray(parsed.photos) ? parsed.photos : [],
      items: Array.isArray(parsed.items) ? parsed.items : [],
      step: [1, 2].includes(parsed.step) ? parsed.step : 1,
    };
  } catch (_) {
    return null;
  }
}

export default function BulkUploadPage() {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  // Rehydrate from localStorage on first render so a refresh keeps the work.
  const restored = useRef(loadDraft());

  // --- Wizard step (1 Photos · 2 Combos · 3 Recognize · 4 Review) ----------
  const [step, setStep] = useState(restored.current?.step || 1);

  // --- Step 1: staged photos ---------------------------------------------
  // photos: [{ id, path, url }] — every image uploaded to bulk-staging/.
  const [photos, setPhotos] = useState(restored.current?.photos || []);
  const [uploading, setUploading] = useState(false);
  const [uploadedCount, setUploadedCount] = useState(restored.current?.photos?.length || 0); // running total across the session
  const [dragActive, setDragActive] = useState(false);
  const [uploadError, setUploadError] = useState('');

  // --- Step 2: organizing -------------------------------------------------
  // items: [{ id, photos: [{ id, path, url }] }] — grouped combos.
  const [items, setItems] = useState(restored.current?.items || []);
  const [selected, setSelected] = useState(() => new Set()); // photo ids currently selected

  // --- Step 3: recognition / draft creation ------------------------------
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState('');
  const [progress, setProgress] = useState({ done: 0, total: 0 });

  // --- Step 4: review the just-created drafts -----------------------------
  // created: [{ id, name, price, photoUrl, ai:{ set_name, number, rarity, product_type, condition, grade } }]
  const [created, setCreated] = useState([]);
  const [saveState, setSaveState] = useState({}); // index -> { status: 'saving'|'saved'|'error', error? }
  const [lightbox, setLightbox] = useState(null); // { photos: [url], index } — tap a review thumbnail to inspect the combo
  const [confirmDelIdx, setConfirmDelIdx] = useState(null); // review row pending delete
  const [deletingIdx, setDeletingIdx] = useState(null);

  // Categories carry a `kind` (single/sealed/accessory/other) — used to file the
  // AI-recognized product_type under the right category.
  const [categories, setCategories] = useState([]);
  useEffect(() => {
    supabase.from('categories').select('id, name, kind').then(({ data }) => setCategories(data || []));
  }, []);

  // Persist the lightweight wizard state whenever it changes.
  useEffect(() => {
    try {
      localStorage.setItem(DRAFT_KEY, JSON.stringify({ photos, items, step }));
    } catch (_) { /* storage full / disabled — non-fatal */ }
  }, [photos, items, step]);

  // Re-hydrate the Step 4 review batch when returning from the standalone
  // editor. Runs once on mount: only when the review batch isn't already in
  // memory, REVIEW_KEY holds ids, and there's no in-progress photos/items draft
  // (a fresh photos/items draft always wins — you're mid-wizard, not reviewing).
  useEffect(() => {
    if (created.length > 0) return;
    const draft = restored.current;
    if (draft && (draft.photos.length > 0 || draft.items.length > 0)) return;

    let ids = [];
    try {
      const raw = localStorage.getItem(REVIEW_KEY);
      const parsed = raw ? JSON.parse(raw) : null;
      if (Array.isArray(parsed)) ids = parsed.filter((x) => typeof x === 'string');
    } catch (_) { return; }
    if (ids.length === 0) return;

    let cancelled = false;
    (async () => {
      const { data, error } = await supabase
        .from('products')
        .select('id, name, price_cents, condition, grade, number, rarity, set_name, category_id, product_media(storage_path, sort_order)')
        .in('id', ids);
      if (cancelled || error || !data) return;

      const byId = {};
      for (const p of data) byId[p.id] = p;

      // Preserve the original batch order and drop any ids that no longer exist.
      const mapped = ids
        .map((pid) => byId[pid])
        .filter(Boolean)
        .map((p) => {
          const product_type =
            p.condition === 'Sealed' ? 'sealed'
              : p.condition === 'Graded' ? 'graded'
                : 'single';
          const sortedMedia = (p.product_media || [])
            .slice()
            .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));
          const photos = sortedMedia.map((m) => mediaUrl(m.storage_path, IMG.card));
          return {
            id: p.id,
            name: p.name,
            price: p.price_cents ? (p.price_cents / 100).toString() : '',
            photoUrl: photos[0] || null,
            photos,
            ai: {
              set_name: p.set_name,
              number: p.number,
              rarity: p.rarity,
              product_type,
              condition: p.condition,
              grade: p.grade,
            },
          };
        });

      if (mapped.length === 0) return;
      setCreated(mapped);
      setSaveState({});
      setStep(4);
    })();

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const groupedPhotoIds = new Set(items.flatMap((it) => it.photos.map((p) => p.id)));
  const ungrouped = photos.filter((p) => !groupedPhotoIds.has(p.id));

  // Wipe everything (state + persisted draft) and return to step 1.
  const startOver = () => {
    setPhotos([]);
    setItems([]);
    setSelected(new Set());
    setUploadedCount(0);
    setUploadError('');
    setCreateError('');
    setCreated([]);
    setSaveState({});
    setStep(1);
    try { localStorage.removeItem(DRAFT_KEY); } catch (_) { /* non-fatal */ }
  };

  // -----------------------------------------------------------------------
  // Step 1 — upload each dropped/selected image to the staging bucket.
  // Errors are collected per file so one bad upload doesn't abort the batch.
  // -----------------------------------------------------------------------
  const uploadFiles = useCallback(async (files) => {
    const imageFiles = files.filter((f) => f.type.startsWith('image/'));
    if (imageFiles.length === 0) return;
    setUploadError('');
    setUploading(true);
    const failures = [];
    for (const file of imageFiles) {
      try {
        const upload = await shrinkImage(file);
        const ext = (upload.name.split('.').pop() || 'jpg').toLowerCase();
        const path = `${STAGING_PREFIX}/${crypto.randomUUID()}.${ext}`;
        const { error: upErr } = await supabase.storage.from(MEDIA_BUCKET).upload(path, upload);
        if (upErr) throw upErr;
        const entry = { id: crypto.randomUUID(), path, url: mediaUrl(path, IMG.card) };
        setPhotos((prev) => [...prev, entry]);
        setUploadedCount((n) => n + 1);
      } catch (e) {
        failures.push(`${file.name}: ${e.message}`);
      }
    }
    setUploading(false);
    if (failures.length) {
      setUploadError(`${failures.length} photo(s) failed to upload. ${failures.slice(0, 3).join(' · ')}`);
    }
  }, []);

  const handleDrop = (e) => {
    e.preventDefault();
    setDragActive(false);
    const files = Array.from(e.dataTransfer.files || []);
    if (files.length) uploadFiles(files);
  };

  const handleSelectFiles = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length) uploadFiles(files);
    e.target.value = '';
  };

  // -----------------------------------------------------------------------
  // Step 2 — selection + combine
  // -----------------------------------------------------------------------
  const toggleSelect = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const combineSelected = () => {
    if (selected.size === 0) return;
    const chosen = ungrouped.filter((p) => selected.has(p.id));
    if (chosen.length === 0) return;
    setItems((prev) => [...prev, { id: crypto.randomUUID(), photos: chosen }]);
    setSelected(new Set());
  };

  const ungroupItem = (itemId) => {
    // Photos return to the ungrouped grid simply by dropping the item —
    // `ungrouped` is derived from which photo ids aren't in any item.
    setItems((prev) => prev.filter((it) => it.id !== itemId));
  };

  // -----------------------------------------------------------------------
  // Step 3 — INTERIM: create plain draft products from the grouped items.
  //
  // ┌──────────────────────────────────────────────────────────────────────┐
  // │  AI PROCESSING SLOTS IN HERE (bulk.md Steps 3–5).                      │
  // │  Instead of creating blank "Untitled" drafts, the grouped items will   │
  // │  be sent to the backend + a vision model for recognition + pricing     │
  // │  research, which pre-fills name/set/rarity/price/etc. Staff then        │
  // │  review (Step 4) and approve (Step 5) to publish real listings.        │
  // │  That path is NOT wired up yet — this blank-draft creation is the       │
  // │  interim behavior so photos + item grouping aren't lost.               │
  // └──────────────────────────────────────────────────────────────────────┘
  // -----------------------------------------------------------------------
  const createDrafts = async () => {
    if (items.length === 0) return;
    if (creating) return; // block re-clicks while a run is in flight
    setCreating(true);
    setCreateError('');
    setProgress({ done: 0, total: items.length });

    // category kind -> id (graded cards file under the single-kind category)
    const catByKind = {};
    for (const c of categories) if (c.kind && !catByKind[c.kind]) catByKind[c.kind] = c.id;

    try {
      let done = 0;
      const results = []; // one entry per created product, for the Step 4 review list
      for (const item of items) {
        // 1) AI recognition — reads name/number/set off the photo (bulk.md Step 3).
        //    Best-effort: if it fails, we still create a draft so photos aren't lost.
        let ai = null;
        try {
          const { data, error } = await supabase.functions.invoke('shinyvault-bulk-ai', {
            body: { image_urls: item.photos.map((p) => p.url) },
          });
          if (!error) ai = data?.result || null;
        } catch (_) { /* fall back to a blank draft */ }

        const type = ai?.product_type || 'single';
        const cardish = type === 'single' || type === 'graded';
        const category_id = catByKind[type === 'graded' ? 'single' : type] || null;
        // Everything is NEW inventory (bulk.md): sealed=Sealed, graded=Graded,
        // raw single=pack-fresh (Near Mint), accessory=no condition.
        const condition =
          type === 'sealed' ? 'Sealed'
            : type === 'graded' ? 'Graded'
              : type === 'accessory' ? null
                : 'Near Mint';

        const num = cardish ? (ai?.number || '').trim() : '';
        const baseName = (ai?.name || '').trim();
        // Singles/graded name = "Card Name Number" (e.g. "Mewtwo GX 78/73").
        // Sealed/accessory: the AI already returns the full formatted product name.
        const name = (cardish && baseName)
          ? `${baseName}${num ? ` ${num}` : ''}`
          : (baseName || `Untitled bulk item ${done + 1}`);
        // Description mirrors staff's terse style: "<name> - <state>".
        const stateTag =
          type === 'sealed' ? 'Sealed'
            : type === 'graded' ? ((ai?.grade || '').trim() || 'Graded')
              : type === 'accessory' ? '' : condition; // single -> "Near Mint"
        const description = stateTag ? `${name} - ${stateTag}` : name;

        const payload = {
          name,
          slug: `bulk-${shortId()}`,
          description,
          price_cents: 0,
          cost_cents: 0,
          quantity_available: 1,
          status: 'draft',
          category_id,
          language: (ai?.language || '').trim() || 'English',
          condition,
          grade: type === 'graded' ? ((ai?.grade || '').trim() || null) : null,
          number: cardish ? (num || null) : null,
          rarity: cardish ? ((ai?.rarity || '').trim() || null) : null,
          set_name: type !== 'accessory' ? ((ai?.set_name || '').trim() || null) : null,
          // Bulk-created products used to land with no weight/dimensions at
          // all, which made the checkout rate quote fall back to 4oz for
          // everything — fine for a single, a real underquote for sealed.
          // Seed a kind-appropriate default so a product is always shippable;
          // the product edit page overrides it once someone weighs the thing.
          ...shippingDefaultsFor(type === 'graded' ? 'single' : type, name),
        };

        const { data: product, error: prodErr } = await supabase
          .from('products').insert(payload).select('id').single();
        if (prodErr) throw prodErr;

        // Attach each staged photo (same storage_path it was uploaded to in Step 1).
        let sortOrder = 0;
        for (const p of item.photos) {
          const { error: mediaErr } = await supabase.from('product_media').insert({
            product_id: product.id, kind: 'photo', storage_path: p.path, sort_order: sortOrder,
          });
          if (mediaErr) throw mediaErr;
          sortOrder += 1;
        }

        // Record this product for the Step 4 review list (price is always blank —
        // AI never sets a price; staff fills it in here before publishing).
        results.push({
          id: product.id,
          name,
          price: '',
          photoUrl: item.photos[0]?.url,
          photos: item.photos.map((p) => p.url),
          ai: {
            set_name: payload.set_name,
            number: payload.number,
            rarity: payload.rarity,
            product_type: type,
            condition,
            grade: payload.grade,
          },
        });

        done += 1;
        setProgress({ done, total: items.length });
      }
      // Success — clear the persisted draft so a refresh on Step 4 starts fresh,
      // reset the working state, and move into the review step (no navigation).
      try { localStorage.removeItem(DRAFT_KEY); } catch (_) { /* non-fatal */ }
      // Persist the created product ids so a trip out to the standalone editor
      // (and back to /admin/products/bulk) re-hydrates this exact review batch.
      try { localStorage.setItem(REVIEW_KEY, JSON.stringify(results.map((c) => c.id))); } catch (_) { /* non-fatal */ }
      setCreated(results);
      setSaveState({});
      setPhotos([]);
      setItems([]);
      setSelected(new Set());
      setCreating(false);
      setStep(4);
    } catch (e) {
      setCreateError(e.message);
      setCreating(false);
    }
  };

  // --- Step 4 — edit name/price on the just-created drafts ----------------
  const updateCreated = (i, patch) =>
    setCreated((prev) => prev.map((c, idx) => (idx === i ? { ...c, ...patch } : c)));

  // Remove a just-created draft from the batch (deletes the product + its photos).
  const removeItem = async (i) => {
    const item = created[i];
    if (!item) return;
    setDeletingIdx(i);
    const { error } = await deleteProduct(item.id);
    setDeletingIdx(null);
    if (error) {
      setSaveState((s) => ({ ...s, [i]: { status: 'error', error: error.message } }));
      setConfirmDelIdx(null);
      return;
    }
    setConfirmDelIdx(null);
    setSaveState({}); // indices shift after removal — clear transient per-row states
    setCreated((prev) => {
      const next = prev.filter((_, idx) => idx !== i);
      try { localStorage.setItem(REVIEW_KEY, JSON.stringify(next.map((c) => c.id))); } catch (_) { /* non-fatal */ }
      return next;
    });
  };

  const saveItem = async (i) => {
    const item = created[i];
    if (!item) return;
    setSaveState((s) => ({ ...s, [i]: { status: 'saving' } }));
    const { error } = await supabase
      .from('products')
      .update({
        name: (item.name || '').trim(),
        price_cents: Math.round(parseFloat(item.price || '0') * 100),
      })
      .eq('id', item.id);
    if (error) setSaveState((s) => ({ ...s, [i]: { status: 'error', error: error.message } }));
    else setSaveState((s) => ({ ...s, [i]: { status: 'saved' } }));
  };

  const totalGrouped = groupedPhotoIds.size;

  // Gate the wizard's Next buttons.
  const canLeaveStep1 = photos.length > 0;
  const canLeaveStep2 = items.length > 0;

  const STEP_LABELS = ['Photos', 'Combos', 'Recognize', 'Review'];

  return (
    <div style={{ maxWidth: 920 }}>
      {/* keyframe for the loading spinner (no generic spin class in index.css) */}
      <style>{'@keyframes sv-bulk-spin{to{transform:rotate(360deg)}}'}</style>

      <Link to="/admin/products/add" style={{ fontSize: '0.85rem', color: 'var(--text-muted)', display: 'inline-block', marginBottom: 16 }}>
        ← Back
      </Link>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
        <div>
          <h1 style={{ fontSize: '1.5rem', fontWeight: 900, marginBottom: 6, display: 'flex', alignItems: 'center', gap: 10 }}>
            <UploadCloud size={24} /> Bulk upload
          </h1>
          <p style={{ color: 'var(--text-muted)', fontSize: '0.9rem', margin: '0 0 24px' }}>
            Drop a pile of photos, group them into items, then create drafts to fill in.
          </p>
        </div>
        {(photos.length > 0 || items.length > 0) && !creating && (
          <button
            type="button"
            onClick={startOver}
            className="sv-btn-outline"
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: '0.78rem', padding: '6px 12px' }}
          >
            <RotateCcw size={14} /> Start over
          </button>
        )}
      </div>

      {/* ---- Step indicator ---------------------------------------------- */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 24, flexWrap: 'wrap' }}>
        {STEP_LABELS.map((label, i) => {
          const n = i + 1;
          const active = step === n;
          const done = step > n;
          return (
            <div key={label} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, opacity: active || done ? 1 : 0.5 }}>
                <span style={{
                  width: 26, height: 26, borderRadius: 999, flexShrink: 0,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: '0.8rem', fontWeight: 800,
                  background: active ? 'var(--accent, #b8860b)' : (done ? 'var(--accent, #b8860b)' : 'transparent'),
                  color: active || done ? '#fff' : 'var(--text-muted)',
                  border: active || done ? '2px solid var(--accent, #b8860b)' : '2px solid var(--border)',
                }}>
                  {done ? <Check size={14} /> : n}
                </span>
                <span style={{ fontSize: '0.85rem', fontWeight: active ? 800 : 600, color: active ? 'var(--text)' : 'var(--text-muted)' }}>
                  {label}
                </span>
              </div>
              {n < STEP_LABELS.length && (
                <span style={{ width: 24, height: 2, background: 'var(--border)', display: 'inline-block' }} />
              )}
            </div>
          );
        })}
      </div>

      {/* ================================================================== */}
      {/* STEP 1 — Add photos                                                */}
      {/* ================================================================== */}
      {step === 1 && (
        <div className="sv-card" style={{ padding: 16, marginBottom: 24 }}>
          <div style={{ fontSize: '0.72rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', marginBottom: 10 }}>
            Step 1 — Add photos
          </div>
          <label
            onDragOver={(e) => { e.preventDefault(); setDragActive(true); }}
            onDragLeave={() => setDragActive(false)}
            onDrop={handleDrop}
            style={{
              display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 8,
              padding: '32px 12px',
              border: `2px dashed ${dragActive ? 'var(--accent, #b8860b)' : 'var(--border)'}`,
              borderRadius: 12,
              background: dragActive ? 'var(--hover, rgba(184,134,11,0.08))' : 'transparent',
              cursor: 'pointer',
              textAlign: 'center',
            }}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              multiple
              onChange={handleSelectFiles}
              style={{ display: 'none' }}
            />
            <UploadCloud size={28} color="var(--text-muted)" />
            <span style={{ fontSize: '0.9rem', color: 'var(--text-muted)' }}>
              Drag photos here, or click to select — hundreds at once is fine
            </span>
          </label>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginTop: 12, fontSize: '0.85rem', flexWrap: 'wrap' }}>
            <span style={{ fontWeight: 700 }}>Uploaded {uploadedCount}</span>
            {uploading && <span style={{ color: 'var(--text-muted)' }}>Uploading…</span>}
            {photos.length > 0 && (
              <span style={{ color: 'var(--text-muted)' }}>
                {ungrouped.length} ungrouped · {totalGrouped} in items
              </span>
            )}
          </div>
          {uploadError && <p style={{ fontSize: '0.8rem', color: 'var(--danger)', marginTop: 8, marginBottom: 0 }}>{uploadError}</p>}
        </div>
      )}

      {/* ================================================================== */}
      {/* STEP 2 — Group into items                                          */}
      {/* ================================================================== */}
      {step === 2 && (
        <>
          <div className="sv-card" style={{ padding: 16, marginBottom: 24 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 12, flexWrap: 'wrap' }}>
              <div style={{ fontSize: '0.72rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', display: 'flex', alignItems: 'center', gap: 8 }}>
                <Images size={16} /> Step 2 — Group into items
              </div>
              <button
                type="button"
                onClick={combineSelected}
                disabled={selected.size === 0}
                className="sv-btn"
                style={{ display: 'inline-flex', alignItems: 'center', gap: 8, opacity: selected.size === 0 ? 0.5 : 1 }}
              >
                <Layers size={16} /> Combine ({selected.size})
              </button>
            </div>

            {ungrouped.length === 0 ? (
              <p style={{ fontSize: '0.85rem', color: 'var(--text-muted)', margin: '4px 0' }}>
                {photos.length === 0
                  ? 'No photos yet — add some in step 1.'
                  : 'All photos are grouped into items below.'}
              </p>
            ) : (
              <>
                <p style={{ fontSize: '0.82rem', color: 'var(--text-muted)', margin: '0 0 12px' }}>
                  Tap photos that belong together (2 = a slab/single, 4 = an ETB/box — any count works), then Combine.
                </p>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(96px, 1fr))', gap: 10 }}>
                  {ungrouped.map((p) => {
                    const isSel = selected.has(p.id);
                    return (
                      <button
                        key={p.id}
                        type="button"
                        onClick={() => toggleSelect(p.id)}
                        style={{
                          position: 'relative', padding: 0, aspectRatio: '1 / 1', cursor: 'pointer',
                          borderRadius: 10, overflow: 'hidden', background: 'var(--bg-subtle, #111)',
                          border: isSel ? '3px solid var(--accent, #b8860b)' : '1px solid var(--border)',
                        }}
                        aria-pressed={isSel}
                      >
                        <img src={p.url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                        {isSel && (
                          <span style={{
                            position: 'absolute', top: 6, right: 6, background: 'var(--accent, #b8860b)', color: '#fff',
                            borderRadius: 999, width: 22, height: 22, display: 'flex', alignItems: 'center', justifyContent: 'center',
                          }}>
                            <Check size={14} />
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              </>
            )}
          </div>

          {/* ---- Items list --------------------------------------------------- */}
          {items.length > 0 && (
            <div className="sv-card" style={{ padding: 16, marginBottom: 24 }}>
              <div style={{ fontSize: '0.72rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', marginBottom: 12 }}>
                Items ({items.length})
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                {items.map((item, i) => (
                  <div key={item.id} style={{ border: '1px solid var(--border)', borderRadius: 10, padding: 12 }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, marginBottom: 10, flexWrap: 'wrap' }}>
                      <span style={{ fontWeight: 700, fontSize: '0.9rem' }}>
                        Item {i + 1} — {item.photos.length} photo{item.photos.length === 1 ? '' : 's'}
                      </span>
                      <button
                        type="button"
                        onClick={() => ungroupItem(item.id)}
                        className="sv-btn-outline"
                        style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: '0.8rem', padding: '6px 12px' }}
                      >
                        <CornerUpLeft size={14} /> Ungroup
                      </button>
                    </div>
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                      {item.photos.map((p) => (
                        <img
                          key={p.id}
                          src={p.url}
                          alt=""
                          style={{ width: 72, height: 72, objectFit: 'cover', borderRadius: 8, border: '1px solid var(--border)' }}
                        />
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}

      {/* ================================================================== */}
      {/* STEP 3 — Recognizing (loading only)                                */}
      {/* ================================================================== */}
      {step === 3 && (
        <div className="sv-card" style={{ padding: 16, marginBottom: 24 }}>
          {createError ? (
            /* ---- Recognition failed — stay here so the error is visible --- */
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center', gap: 14, padding: '40px 16px' }}>
              <h2 style={{ fontSize: '1.1rem', fontWeight: 900, margin: 0 }}>Recognition stopped</h2>
              <p style={{ fontSize: '0.85rem', color: 'var(--danger)', margin: 0 }}>{createError}</p>
              <button
                type="button"
                onClick={() => { setCreateError(''); setStep(2); }}
                className="sv-btn-outline"
                style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}
              >
                <ArrowLeft size={16} /> Back to combos
              </button>
            </div>
          ) : (
            /* ---- Loading screen — createDrafts runs on entering this step - */
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center', gap: 14, padding: '40px 16px' }}>
              <Loader2 size={40} color="var(--accent, #b8860b)" style={{ animation: 'sv-bulk-spin 0.9s linear infinite' }} />
              <h2 style={{ fontSize: '1.15rem', fontWeight: 900, margin: 0 }}>Recognizing your items…</h2>
              <p style={{ fontSize: '0.88rem', color: 'var(--text-muted)', margin: 0 }}>
                Reading each photo and creating drafts with your photos attached. Hang tight — don't close this tab.
              </p>
              <div style={{ fontSize: '1.4rem', fontWeight: 900, color: 'var(--accent, #b8860b)' }}>
                {progress.done} / {progress.total} done
              </div>
              {progress.total > 0 && (
                <div style={{ width: '100%', maxWidth: 320, height: 8, borderRadius: 999, background: 'var(--border)', overflow: 'hidden' }}>
                  <div style={{
                    width: `${Math.round((progress.done / progress.total) * 100)}%`,
                    height: '100%', background: 'var(--accent, #b8860b)', transition: 'width 0.2s ease',
                  }} />
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* ================================================================== */}
      {/* STEP 4 — Review the just-created drafts                             */}
      {/* ================================================================== */}
      {step === 4 && (
        <div className="sv-card" style={{ padding: 16, marginBottom: 24 }}>
          <div style={{ fontSize: '0.72rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', marginBottom: 8 }}>
            Step 4 — Review
          </div>
          <p style={{ fontSize: '0.82rem', color: 'var(--text-muted)', margin: '0 0 16px' }}>
            Set a price on each item, then click Done. Items are saved as drafts — they won't be public
            until you publish them from the products list.
          </p>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {created.map((item, i) => {
              const facts = [
                item.ai?.product_type,
                item.ai?.set_name,
                item.ai?.number,
                item.ai?.rarity,
                item.ai?.condition,
              ].filter(Boolean).join(' · ');
              const st = saveState[i];
              return (
                <div key={item.id} style={{ display: 'flex', gap: 12, border: '1px solid var(--border)', borderRadius: 10, padding: 12, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                  {(() => {
                    const shots = (item.photos && item.photos.length) ? item.photos : (item.photoUrl ? [item.photoUrl] : []);
                    return shots.length ? (
                      <button
                        type="button"
                        onClick={() => setLightbox({ photos: shots, index: 0 })}
                        aria-label="View photos"
                        style={{ position: 'relative', padding: 0, border: 'none', background: 'none', cursor: 'zoom-in', flexShrink: 0, lineHeight: 0 }}
                      >
                        <img src={shots[0]} alt="" style={{ width: 64, height: 64, objectFit: 'cover', borderRadius: 8, border: '1px solid var(--border)', display: 'block' }} />
                        <span style={{ position: 'absolute', bottom: 3, right: 3, display: 'inline-flex', padding: 2, borderRadius: 6, background: 'color-mix(in srgb, var(--bg) 78%, transparent)', border: '1px solid var(--border)', color: 'var(--text)' }}>
                          <ZoomIn size={12} />
                        </span>
                        {shots.length > 1 && (
                          <span style={{ position: 'absolute', top: 3, right: 3, fontSize: '0.6rem', fontWeight: 800, padding: '1px 5px', borderRadius: 999, background: 'color-mix(in srgb, var(--bg) 78%, transparent)', border: '1px solid var(--border)', color: 'var(--text)' }}>
                            {shots.length}
                          </span>
                        )}
                      </button>
                    ) : (
                      <div style={{ width: 64, height: 64, borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg-subtle, #111)', flexShrink: 0 }} />
                    );
                  })()}
                  <div style={{ flex: '1 1 240px', display: 'flex', flexDirection: 'column', gap: 8, minWidth: 220 }}>
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                      <textarea
                        value={item.name}
                        onChange={(e) => updateCreated(i, { name: e.target.value })}
                        placeholder="Name"
                        rows={2}
                        className="sv-input"
                        style={{ flex: '1 1 100%', width: '100%', minWidth: 160, resize: 'vertical', lineHeight: 1.35 }}
                      />
                      <div style={{ position: 'relative', flex: '1 1 100px', minWidth: 100 }}>
                        <span style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)', fontSize: '0.9rem', pointerEvents: 'none' }}>$</span>
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          value={item.price}
                          onChange={(e) => updateCreated(i, { price: e.target.value })}
                          placeholder="0.00"
                          className="sv-input"
                          style={{ width: '100%', paddingLeft: 22 }}
                        />
                      </div>
                    </div>
                    {facts && (
                      <div style={{ fontSize: '0.76rem', color: 'var(--text-muted)' }}>{facts}</div>
                    )}
                    <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                      <button
                        type="button"
                        onClick={() => saveItem(i)}
                        disabled={st?.status === 'saving'}
                        className="sv-btn"
                        style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: '0.8rem', padding: '6px 14px', opacity: st?.status === 'saving' ? 0.6 : 1 }}
                      >
                        {st?.status === 'saving' ? 'Saving…' : 'Save'}
                      </button>
                      {st?.status === 'saved' && (
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: '0.8rem', color: 'var(--success, #22c55e)', fontWeight: 700 }}>
                          <Check size={14} /> saved
                        </span>
                      )}
                      {st?.status === 'error' && (
                        <span style={{ fontSize: '0.78rem', color: 'var(--danger)' }}>{st.error}</span>
                      )}
                      <Link to={`/admin/products/${item.id}?from=bulk`} style={{ fontSize: '0.8rem', color: 'var(--accent, #b8860b)', fontWeight: 700 }}>
                        Edit details
                      </Link>
                      <button
                        type="button"
                        onClick={() => setConfirmDelIdx(i)}
                        disabled={deletingIdx === i}
                        aria-label="Remove item"
                        style={{ marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: '0.8rem', padding: '6px 12px', background: 'none', border: '1px solid var(--border)', borderRadius: 8, color: 'var(--danger)', cursor: 'pointer' }}
                      >
                        <Trash2 size={14} /> {deletingIdx === i ? 'Removing…' : 'Remove'}
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          <div style={{ marginTop: 20 }}>
            <button
              type="button"
              onClick={() => {
                try { localStorage.removeItem(REVIEW_KEY); } catch (_) { /* non-fatal */ }
                try { localStorage.removeItem(DRAFT_KEY); } catch (_) { /* non-fatal */ }
                navigate('/admin/products');
              }}
              className="sv-btn"
              style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}
            >
              Done — go to products <ArrowRight size={16} />
            </button>
          </div>
        </div>
      )}

      {/* ---- Wizard nav (steps 1–2 only) ---------------------------------- */}
      {(step === 1 || step === 2) && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 24 }}>
          <button
            type="button"
            onClick={() => setStep((s) => Math.max(1, s - 1))}
            disabled={step === 1}
            className="sv-btn-outline"
            style={{ display: 'inline-flex', alignItems: 'center', gap: 8, opacity: step === 1 ? 0.4 : 1 }}
          >
            <ArrowLeft size={16} /> Back
          </button>
          {step === 1 && (
            <button
              type="button"
              onClick={() => setStep(2)}
              disabled={!canLeaveStep1}
              className="sv-btn"
              style={{ display: 'inline-flex', alignItems: 'center', gap: 8, opacity: !canLeaveStep1 ? 0.5 : 1 }}
            >
              Next <ArrowRight size={16} />
            </button>
          )}
          {step === 2 && (
            <button
              type="button"
              onClick={() => { setCreateError(''); setStep(3); createDrafts(); }}
              disabled={!canLeaveStep2}
              className="sv-btn"
              style={{ display: 'inline-flex', alignItems: 'center', gap: 8, opacity: !canLeaveStep2 ? 0.5 : 1 }}
            >
              Send to review <ArrowRight size={16} />
            </button>
          )}
        </div>
      )}

      {lightbox && (
        <Lightbox
          photos={lightbox.photos}
          startIndex={lightbox.index}
          alt="Item photos"
          onClose={() => setLightbox(null)}
        />
      )}

      {confirmDelIdx !== null && created[confirmDelIdx] && (
        <ConfirmDialog
          title="Remove this item?"
          message={`"${created[confirmDelIdx].name}" and its photos will be permanently deleted. This can't be undone.`}
          confirmLabel="Remove"
          busy={deletingIdx === confirmDelIdx}
          onConfirm={() => removeItem(confirmDelIdx)}
          onCancel={() => { if (deletingIdx === null) setConfirmDelIdx(null); }}
        />
      )}
    </div>
  );
}
